import React, { useState, useEffect } from "react";
import {
  IonButton,
  IonCard,
  IonCardContent,
  IonCardHeader,
  IonCardTitle,
  IonCheckbox,
  IonContent,
  IonIcon,
  IonInput,
  IonItem,
  IonLabel,
  IonPage,
  IonToast,
  IonLoading,
  IonText,
  IonGrid,
  IonRow,
  IonCol,
} from "@ionic/react";
import {
  eyeOutline,
  eyeOffOutline,
  mailOutline,
  callOutline,
  lockClosedOutline,
  logInOutline,
  logoGoogle,
  logoGithub,
} from "ionicons/icons";
import { useTranslation } from "react-i18next";
import { useHistory } from "react-router-dom";
import { ApiService } from "../services/api.service";
import "./Login.css";

interface LoginProps {
  onLoginSuccess?: () => void;
}

const Login: React.FC<LoginProps> = ({ onLoginSuccess }) => {
  const { t } = useTranslation();
  const history = useHistory();

  const [emailOrMobile, setEmailOrMobile] = useState<string>("");
  const [password, setPassword] = useState<string>("");
  const [showPassword, setShowPassword] = useState<boolean>(false);
  const [rememberMe, setRememberMe] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [showToast, setShowToast] = useState<boolean>(false);
  const [toastMessage, setToastMessage] = useState<string>("");
  const [toastColor, setToastColor] = useState<string>("success");

  // Load remembered user data on component mount
  useEffect(() => {
    const savedEmailOrMobile = localStorage.getItem("userEmailOrMobile");
    const savedRememberMe = localStorage.getItem("rememberMe");

    if (savedRememberMe === "true" && savedEmailOrMobile) {
      setEmailOrMobile(savedEmailOrMobile);
      setRememberMe(true);
    }
  }, []);

  const handleLogin = async () => {
    if (!emailOrMobile || !password) {
      setToastMessage(t("login.validation.required"));
      setToastColor("danger");
      setShowToast(true);
      return;
    }

    if (!isValidEmailOrMobile(emailOrMobile)) {
      setToastMessage(t("login.validation.invalidEmailOrMobile"));
      setToastColor("danger");
      setShowToast(true);
      return;
    }

    setIsLoading(true);

    try {
      // Prepare login data
      const loginData = {
        emailOrMobile: emailOrMobile.trim(),
        password: password,
        rememberMe: rememberMe,
      };

      // Make API call to backend
      const response = await ApiService.post("/login", loginData);

      // Handle successful response
      if (response.data && response.status === 200) {
        if (response.data.authToken) {
          const token = response.data.token || response.data.authToken;
          ApiService.setAuthToken(token);
        }

        // Store user data in localStorage for app use
        if (response.data.user) {
          const userData = {
            id: response.data.user.id || response.data.user.userId,
            firstName: response.data.user.firstName,
            lastName: response.data.user.lastName,
            emailOrMobile: response.data.user.emailOrMobile,
            lastLogin: new Date().toISOString(),
          };

          localStorage.setItem("userData", JSON.stringify(userData));

          // Handle remember me functionality
          if (rememberMe) {
            localStorage.setItem("userEmailOrMobile", emailOrMobile);
            localStorage.setItem("rememberMe", "true");
          } else {
            // Clear remember me data if not checked
            localStorage.removeItem("userEmailOrMobile");
            localStorage.removeItem("rememberMe");
          }
        }

        setToastMessage(response.data.message || t("login.success"));
        setToastColor("success");
        setShowToast(true);

        console.log("Login successful:", response.data);

        // Navigate to home page after successful login
        setTimeout(() => {
          if (onLoginSuccess) {
            onLoginSuccess();
          }
          history.push("/tab1");
        }, 1500);
      } else {
        throw new Error(response.data?.error || "Login failed");
      }
    } catch (error: any) {
      console.error("Login error:", error);

      // Handle specific error cases
      let errorMessage = t("login.errors.general");

      if (error.response?.data?.error) {
        errorMessage = error.response.data.error;
      } else if (error.response?.status === 401) {
        errorMessage = t("login.errors.invalidCredentials");
      } else if (error.response?.status === 404) {
        errorMessage = t("login.errors.userNotFound");
      } else if (error.response?.status === 423) {
        errorMessage = t("login.errors.accountLocked");
      } else if (error.response?.status === 429) {
        errorMessage = t("login.errors.tooManyAttempts");
      } else if (error.response?.status >= 500) {
        errorMessage = t("login.errors.serverError");
      } else if (error.code === "NETWORK_ERROR" || !navigator.onLine) {
        errorMessage = t("login.errors.networkError");
      }

      setToastMessage(errorMessage);
      setToastColor("danger");
      setShowToast(true);
    } finally {
      setIsLoading(false);
    }
  };

  const isValidEmailOrMobile = (input: string): boolean => {
    // Email validation regex
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    // Mobile number validation regex (supports various formats)
    // Matches: +1234567890, 1234567890, +91-1234567890, etc.
    const mobileRegex = /^(\+\d{1,3}[-.\s]?)?\d{10,15}$/;

    return (
      emailRegex.test(input) || mobileRegex.test(input.replace(/[-.\s]/g, ""))
    );
  };

  const handleForgotPassword = () => {
    setToastMessage(t("login.forgotPassword.sent"));
    setToastColor("primary");
    setShowToast(true);
  };

  const handleSignUp = () => {
    history.push("/signup");
  };

  return (
    <IonPage>
      <IonContent className="login-content">
        <div className="login-container">
          <IonCard className="login-card">
            <IonCardHeader className="login-header">
              <IonCardTitle className="welcome-title">
                {t("login.welcomeBack")}
              </IonCardTitle>
              <IonText color="medium" className="subtitle">
                {t("login.signInToAccount")}
              </IonText>
            </IonCardHeader>

            <IonCardContent className="login-form">
              {/* Email or Mobile Input */}
              <div className="input-group">
                <IonLabel className="input-label">
                  {t("login.emailOrMobile")}
                </IonLabel>
                <IonItem className="login-input-item">
                  <IonIcon
                    icon={
                      isValidEmailOrMobile(emailOrMobile) &&
                      emailOrMobile.includes("@")
                        ? mailOutline
                        : callOutline
                    }
                    slot="start"
                    color="medium"
                  />
                  <IonInput
                    type="text"
                    placeholder={t("login.enterEmailOrMobile")}
                    value={emailOrMobile}
                    onIonInput={(e) => setEmailOrMobile(e.detail.value!)}
                    className="login-input"
                    clearInput
                  />
                </IonItem>
              </div>

              {/* Password Input */}
              <div className="input-group">
                <IonLabel className="input-label">
                  {t("login.password")}
                </IonLabel>
                <IonItem className="login-input-item">
                  <IonIcon
                    icon={lockClosedOutline}
                    slot="start"
                    color="medium"
                  />
                  <IonInput
                    type={showPassword ? "text" : "password"}
                    placeholder={t("login.enterPassword")}
                    value={password}
                    onIonInput={(e) => setPassword(e.detail.value!)}
                    className="login-input"
                    clearInput
                  />
                  <IonButton
                    fill="clear"
                    slot="end"
                    onClick={() => setShowPassword(!showPassword)}
                  >
                    <IonIcon
                      icon={showPassword ? eyeOffOutline : eyeOutline}
                      color="medium"
                    />
                  </IonButton>
                </IonItem>
              </div>

              {/* Remember Me and Forgot Password */}
              <div className="login-options">
                <IonItem lines="none" className="remember-me-item">
                  <IonCheckbox
                    checked={rememberMe}
                    onIonChange={(e) => setRememberMe(e.detail.checked)}
                    slot="start"
                  />
                  <IonLabel className="remember-me-label">
                    {t("login.rememberMe")}
                  </IonLabel>
                </IonItem>

                <IonButton
                  fill="clear"
                  size="small"
                  className="forgot-password-btn"
                  onClick={handleForgotPassword}
                >
                  {t("login.forgotPassword.link")}
                </IonButton>
              </div>

              {/* Sign In Button */}
              <IonButton
                expand="block"
                size="large"
                className="sign-in-btn"
                onClick={handleLogin}
                disabled={isLoading}
              >
                <IonIcon icon={logInOutline} slot="start" />
                {t("login.signIn")}
              </IonButton>

              {/* Sign Up Link */}
              <div className="signup-section">
                <IonText color="medium">{t("login.noAccount")}</IonText>
                <IonButton
                  fill="clear"
                  size="small"
                  onClick={handleSignUp}
                  className="signup-btn"
                >
                  {t("login.signUp.link")}
                </IonButton>
              </div>
            </IonCardContent>
          </IonCard>
        </div>

        <IonLoading isOpen={isLoading} message={t("login.signingIn")} />

        <IonToast
          isOpen={showToast}
          onDidDismiss={() => setShowToast(false)}
          message={toastMessage}
          duration={3000}
          color={toastColor}
        />
      </IonContent>
    </IonPage>
  );
};

export default Login;
